import { Suspense, useState, useEffect, useRef } from 'react';
import { Link, Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { Plus } from 'lucide-react';
import { PokeballSpinner } from '../components/PokeballSpinner';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { useClaimPendingGifts } from '../features/gifts/queries';
import { useGetVoucherBalance } from '../features/vouchers/queries';
import { useGetProfile } from '../features/profile/queries';
import { useGetPackInventory } from '../features/packs/usePackInventory';
import { TopUpModal } from '../features/points/TopUpModal';
import { FeedbackModal } from '../components/FeedbackModal';
import { MobileBottomNav } from '../components/MobileBottomNav';
import { AccountPopover } from '../features/account/AccountPopover';
import { client } from '../lib/amplify-client';
import { queryClient } from '../lib/query-client';
import { useTheme } from '../hooks/useTheme';
import { useAnalyticsPageView } from '../hooks/useAnalyticsPageView';
import { useHydrated } from '../hooks/useHydrated';
import { useCaptureRedditClickId } from '../lib/analytics';

function useInitPackWeights() {
  return useMutation({
    mutationFn: async () => {
      /* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any */
      const result: { data?: unknown; errors?: { message: string }[] } =
        await (client.mutations as any).initPackWeights(
          {},
          { authMode: 'userPool' },
        );
      /* eslint-enable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any */
      if (result.errors?.length) {
        throw new Error(result.errors[0]?.message ?? 'initPackWeights failed');
      }
      return result.data as { initialized: boolean };
    },
  });
}

export function RootLayout(): JSX.Element {
  useTheme();

  const { signOut, authStatus, user } = useAuth();
  const location = useLocation();
  const hydrated = useHydrated();
  useAnalyticsPageView(user?.userId);
  useCaptureRedditClickId();

  const [topUpOpen, setTopUpOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [authPopoverOpen, setAuthPopoverOpen] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(() => {
    try {
      return localStorage.getItem('pullit-terms-accepted') === 'true';
    } catch {
      return false;
    }
  });

  const signedIn = authStatus === 'authenticated';
  // Gate auth-aware UI on hydration to avoid SSG hydration mismatch.
  // Server renders as anonymous; first client render must also render as
  // anonymous (hydrated=false). Post-effect re-render flips to real state.
  const showAuthAware = hydrated && signedIn;
  const { data: profile } = useGetProfile(signedIn);
  const voucherQuery = useGetVoucherBalance(signedIn);
  const voucherBalance = voucherQuery.data?.balance ?? 0;
  const pointsBalance = voucherQuery.data?.points ?? 0;
  const packInventory = useGetPackInventory(signedIn);
  const totalPacks = (packInventory.data ?? []).reduce((sum, e) => sum + e.quantity, 0);
  const claimGifts = useClaimPendingGifts();
  const initWeights = useInitPackWeights();
  const claimedRef = useRef(false);
  useEffect(() => {
    if (signedIn && !claimedRef.current) {
      claimedRef.current = true;
      claimGifts.mutate();
      initWeights.mutate();
    }
  }, [signedIn, claimGifts, initWeights]);

  if (
    showAuthAware &&
    profile !== undefined &&
    profile?.profile_onboarded === false &&
    location.pathname !== '/welcome' &&
    location.pathname !== '/sign-in'
  ) {
    return <Navigate to="/welcome" replace />;
  }

  const handleSignOut = (): void => {
    setAuthPopoverOpen(false);
    queryClient.clear();
    // signOut() re-throws on amplifySignOut rejection (network/stale
    // token). Local state has already flipped to unauthenticated by
    // contract; suppress the floating-promise lint rather than
    // surface an error toast for an action the user already saw take
    // effect.
    void signOut();
  };

  const handleTopUp = (): void => {
    setAuthPopoverOpen(false);
    setTopUpOpen(true);
  };

  const handleFeedback = (): void => {
    setAuthPopoverOpen(false);
    setFeedbackOpen(true);
  };

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      {/* Mobile compact top bar (<lg) */}
      <header className="flex items-center gap-3 px-4 py-3 border-b border-border lg:hidden h-14">
        <Link
          to="/"
          className="font-display text-base font-bold text-text-0 no-underline whitespace-nowrap"
        >
          Pull It
        </Link>
        <div className="ml-auto flex items-center gap-2">
          {showAuthAware ? (
            <>
              <button
                type="button"
                onClick={() => setTopUpOpen(true)}
                aria-label="Top up points"
                data-testid="points-balance-mobile"
                className="inline-flex items-center gap-1 rounded-full border border-accent-pull/40 px-2.5 py-0.5 text-xs font-semibold text-accent-pull hover:bg-accent-pull/10 transition-colors ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <Plus className="size-3" />
                {pointsBalance} pts
              </button>
              <Popover open={authPopoverOpen} onOpenChange={setAuthPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    size="icon"
                    variant="outline"
                    aria-label="Account menu"
                    data-testid="auth-chip"
                  >
                    <span className="text-sm font-semibold">
                      {(user?.signInDetails?.loginId?.[0] ?? 'A').toUpperCase()}
                    </span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="end" sideOffset={8}>
                  <AccountPopover
                    pointsBalance={pointsBalance}
                    voucherBalance={voucherBalance}
                    onTopUp={handleTopUp}
                    onFeedback={handleFeedback}
                    onSignOut={handleSignOut}
                  />
                </PopoverContent>
              </Popover>
            </>
          ) : (
            <Button asChild size="sm">
              <Link to="/sign-in" className="no-underline">
                Sign in
              </Link>
            </Button>
          )}
        </div>
      </header>

      {/* Desktop header (lg+) — unchanged from before */}
      <header className="hidden lg:flex items-center gap-6 px-6 py-4 border-b border-border">
        <Link
          to="/"
          className="font-display text-h2 font-bold text-text-0 no-underline"
        >
          Pull It Packs
        </Link>
        <nav aria-label="Primary" className="flex items-center gap-6">
          <Link
            to="/"
            className="text-text-1 text-small font-medium no-underline hover:text-text-0"
          >
            Browse
          </Link>
          <Link
            to="/marketplace"
            className="text-text-1 text-small font-medium no-underline hover:text-text-0"
          >
            Market
          </Link>
          <Link
            to={showAuthAware ? '/account' : '/sign-in'}
            className="text-text-1 text-small font-medium no-underline hover:text-text-0 inline-flex items-center gap-1"
          >
            Account
            {showAuthAware && totalPacks > 0 && (
              <Badge
                variant="default"
                className="text-xs px-1.5 py-0"
                data-testid="nav-packs-badge"
              >
                {totalPacks}
              </Badge>
            )}
          </Link>
        </nav>
        <nav aria-label="Account" className="ml-auto flex items-center gap-3">
          <Link
            to="/about"
            className="text-text-1 text-small font-medium no-underline hover:text-text-0"
          >
            About
          </Link>
          <button
            type="button"
            onClick={() => setFeedbackOpen(true)}
            className="text-text-1 text-small font-medium hover:text-text-0 bg-transparent border-0 p-0 cursor-pointer rounded-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            data-testid="header-feedback-button"
          >
            Feedback
          </button>
          {showAuthAware ? (
            <>
              {voucherBalance > 0 ? (
                <Badge variant="outline" data-testid="voucher-balance">
                  {voucherBalance} voucher{voucherBalance !== 1 ? 's' : ''}
                </Badge>
              ) : null}
              <button
                type="button"
                onClick={() => setTopUpOpen(true)}
                aria-label="Top up points"
                data-testid="points-balance"
                className="inline-flex items-center gap-1 rounded-full border border-accent-pull/40 px-2.5 py-0.5 text-xs font-semibold text-accent-pull hover:bg-accent-pull/10 transition-colors ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <Plus className="size-3" />
                {pointsBalance} pts
              </button>
              <Button
                variant="secondary"
                size="sm"
                onClick={handleSignOut}
              >
                Sign out
              </Button>
            </>
          ) : (
            <Button asChild>
              <Link to="/sign-in" className="no-underline">
                Sign in
              </Link>
            </Button>
          )}
        </nav>
      </header>

      <main className="flex-1 pb-20 lg:pb-0">
        <Suspense
          fallback={
            <div className="flex items-center justify-center gap-2 p-8 text-text-2">
              <PokeballSpinner className="size-4" />
              Loading...
            </div>
          }
        >
          <Outlet />
        </Suspense>
      </main>

      {hydrated && signedIn && !termsAccepted && location.pathname !== '/terms' && (
        <Dialog open>
          <DialogContent
            onPointerDownOutside={(e) => e.preventDefault()}
            onEscapeKeyDown={(e) => e.preventDefault()}
            showCloseButton={false}
          >
            <DialogHeader>
              <DialogTitle>Terms and Conditions</DialogTitle>
              <DialogDescription>
                Please accept our Terms and Conditions to continue.
              </DialogDescription>
            </DialogHeader>
            <a
              href="/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent-primary text-small underline"
            >
              Read Terms and Conditions
            </a>
            <Button
              onClick={() => {
                try {
                  localStorage.setItem('pullit-terms-accepted', 'true');
                } catch {
                  /* localStorage may be unavailable */
                }
                setTermsAccepted(true);
              }}
            >
              I agree to the Terms and Conditions
            </Button>
          </DialogContent>
        </Dialog>
      )}

      <TopUpModal isOpen={topUpOpen} onClose={() => setTopUpOpen(false)} />
      <FeedbackModal open={feedbackOpen} onOpenChange={setFeedbackOpen} />

      <MobileBottomNav signedIn={showAuthAware} totalPacks={totalPacks} />

      <footer className="border-t border-border px-6 py-6 hidden lg:block">
        <div className="max-w-6xl mx-auto flex flex-col items-center gap-2">
          <div className="flex gap-4">
            <Link to="/" className="text-text-2 text-small no-underline hover:text-text-0">Browse</Link>
            <Link to="/marketplace" className="text-text-2 text-small no-underline hover:text-text-0">Market</Link>
            <Link to="/terms" className="text-text-2 text-small no-underline hover:text-text-0">Terms</Link>
            <a href="mailto:info@pullitpacks.com" className="text-text-2 text-small no-underline hover:text-text-0">Support</a>
          </div>
          {/* TODO: bump to next year on annual refresh. Hardcoded to avoid build-time/runtime year hydration mismatch. */}
          <p className="text-text-2 text-small">
            &copy; 2026 Pull It Packs. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
